import React, { useState } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  CircularProgress,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { CheckCircle, Warning } from "@mui/icons-material";
import { StudentListItem } from "../../../../types/StudentModel";
import EmailAvatar from "../../../../components/EmailAvatar";

interface ChangesSummaryDialogProps {
  open: boolean;
  onClose: () => void;
  addedStudents: StudentListItem[];
  removedStudents: StudentListItem[];
  courseStarted: boolean;
  onConfirm: (emailOptions: {
    sendToAdded: boolean;
    sendToRemoved: boolean;
  }) => Promise<void>;
  onCancel: () => void;
}

const ChangesSummaryDialog: React.FC<ChangesSummaryDialogProps> = ({
  open,
  onClose,
  addedStudents,
  removedStudents,
  courseStarted,
  onConfirm,
  onCancel,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sendEmailToNewStudents, setSendEmailToNewStudents] = useState(
    addedStudents.length > 0
  );
  const [sendEmailToRemovedStudents, setSendEmailToRemovedStudents] =
    useState(false);

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      await onConfirm({
        sendToAdded: sendEmailToNewStudents,
        sendToRemoved: sendEmailToRemovedStudents,
      });
      onClose();
    } catch (error) {
      console.error("Error saving changes:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={isSubmitting}
    >
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Warning color="warning" sx={{ mr: 1 }} />
          Confirm Student Changes
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Typography paragraph>
          Please review the following changes to student enrollments:
        </Typography>

        <Grid container spacing={2}>
          {addedStudents.length > 0 && (
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography
                  variant="subtitle1"
                  color="primary"
                  fontWeight={500}
                  gutterBottom
                >
                  {addedStudents.length} Student
                  {addedStudents.length !== 1 ? "s" : ""} to be Added
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={sendEmailToNewStudents}
                      onChange={(e) =>
                        setSendEmailToNewStudents(e.target.checked)
                      }
                      color="primary"
                    />
                  }
                  label="Send welcome emails"
                />
              </Box>
              <Paper
                variant="outlined"
                sx={{ p: 2, maxHeight: 200, overflow: "auto" }}
              >
                <List dense disablePadding>
                  {addedStudents.map((student) => (
                    <ListItem key={student.id} divider>
                      <ListItemAvatar>
                        <EmailAvatar email={student.name} size={32} />
                      </ListItemAvatar>
                      <ListItemText
                        primary={student.name}
                        secondary={student.email}
                        primaryTypographyProps={{ fontWeight: 500 }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>
          )}

          {removedStudents.length > 0 && (
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography
                  variant="subtitle1"
                  color="error"
                  fontWeight={500}
                  gutterBottom
                >
                  {removedStudents.length} Student
                  {removedStudents.length !== 1 ? "s" : ""} to be{" "}
                  {courseStarted ? "Unenrolled" : "Removed"}
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={sendEmailToRemovedStudents}
                      onChange={(e) =>
                        setSendEmailToRemovedStudents(e.target.checked)
                      }
                      color="primary"
                    />
                  }
                  label={`Send ${
                    courseStarted ? "unenrollment" : "removal"
                  } notifications`}
                />
              </Box>
              <Paper
                variant="outlined"
                sx={{ p: 2, maxHeight: 200, overflow: "auto" }}
              >
                <List dense disablePadding>
                  {removedStudents.map((student) => (
                    <ListItem key={student.id} divider>
                      <ListItemAvatar>
                        <EmailAvatar email={student.name} size={32} />
                      </ListItemAvatar>
                      <ListItemText
                        primary={student.name}
                        secondary={student.email}
                        primaryTypographyProps={{ fontWeight: 500 }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 2, justifyContent: "space-between" }}>
        <Button onClick={onCancel} color="inherit" disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          color="primary"
          variant="contained"
          disabled={isSubmitting}
          startIcon={
            isSubmitting ? <CircularProgress size={20} /> : <CheckCircle />
          }
        >
          {isSubmitting ? "Saving..." : "Confirm Changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
export default ChangesSummaryDialog;
